/* Verbatim port of the §1 IMPACT-view timeline subsystem from
 * service/review/index.html: captureFor (~1573-1578, placed here rather
 * than views/search.ts — its only caller is cityCards below, not
 * renderSearch; documented per the brief's "moving it between these
 * modules is fine — document it" allowance), TL_STATE (~1667-1668),
 * wireImpControls (~1669-1706), syncTimeOnlyControls (~1707-1710),
 * refreshImpCanvas (~1711-1720), cityCards (~1721-1734), timeCards
 * (~1735-1748), loadTimeline (~1749-1766), IMP_FEATURE_LABEL (~1768-1783),
 * impMarkup (~1784-1800), renderImpCanvas (~1941-2069), renderKeyDomains
 * (~2071-2093).
 *
 * PARAMETERIZED (rule a): every function that read the global `STATE` now
 * takes `state: AppPayloadState` as an explicit parameter (appended after
 * the function's existing parameters, so call sites read naturally, same
 * placement convention lib/icons.ts used for `apiBase`).
 *
 * PARAMETERIZED: `SERP_AXIS`/`SERP_TERM` reads become getSerpAxis()/
 * getSerpTerm() (views/search.ts); the axis/term button handlers'
 * assignments become setSerpAxis(...)/setSerpTerm(...) — see search.ts's
 * module doc for why (ES import bindings are read-only).
 *
 * loadTimeline's `/impact/timeline` fetch goes through api/impact.ts's
 * fetchTimeline() (F2a) with the exact params the old call site built:
 * brand stripped of any " (..." suffix, max_polls '10'.
 *
 * DEVIATION: wireImpControls originally computed `const terms = [...new
 * Set(rows.map(r => r.term))];` (~1670) but never read it — every use in
 * the function body is `locs`, derived independently via `rows.filter(r =>
 * r.term === SERP_TERM)`. A pure dead local; dropped here (TS
 * `noUnusedLocals`), behavior-neutral.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * `chip()` → `chipT()`. `renderKeyDomains`'s row wiring is fully owned by
 * this function (rows it renders itself) so it converts to `@click`, same
 * precedent as every prior wave's data-table rows.
 *
 * WAVE-C-SPECIFIC CARVE-OUT (renderImpCanvas — the brief's own instruction:
 * "follow the graph.ts precedent: imperative internals stay; only
 * template-building converts"): the `.imp-card`/`.imp-result` markup itself
 * (rowFor + the card wrapper) converts to lit TemplateResults, rendered via
 * one `render(html\`${cards}\`, grid)` call. The THREE post-render wiring
 * loops immediately below it (per-result click → openResultPopover,
 * cross-card hover highlight by shared data-nurl, the page-2 disclosure
 * toggle) stay verbatim `querySelectorAll(...).forEach(el => el.onclick =
 * ...)` imperative code, unconverted — same treatment ui/graph.ts's
 * bindGraphEvents got: the hover handler's whole point is reaching OTHER
 * cards' rows sharing the same normalized URL (a genuine cross-element,
 * whole-grid concern, not a single element's own `@click`), and forcing it
 * into `@mouseenter`/`@mouseleave` per-row bindings would add real
 * behavioral risk for zero benefit. Convention #9's carve-out, applied
 * explicitly by this wave's brief.
 *
 * WAVE-C-SPECIFIC CARVE-OUT (wireImpControls): every button/select it wires
 * (#modeSeg, #zoomSeg, #axisSeg, #termSeg, #tlLoc, #tlStart/#tlEnd,
 * #tlHighlight) is rendered by views/search.ts's OWN template, not by this
 * function — wireImpControls only ever attaches behavior to markup a
 * SIBLING module owns (search.ts's renderSearch() calls wireImpControls()
 * right after its own render()). That's the same "handler targeting an
 * element outside the section container" exception convention #9 already
 * carves out, so the onclick/onchange assignments and the manual
 * `classList.toggle('on', ...)` siblings-repaint stay exactly as imperative
 * post-render wiring — moving them into search.ts's template would require
 * search.ts to re-implement timeline.ts's own state machine (TL_STATE)
 * inline, which is a bigger, riskier change than this task calls for. Only
 * the two PURE template-building sites inside wireImpControls (`locSel`'s
 * `<option>` list, `legend`'s swatch list — both simple, self-contained,
 * no wiring attached to the generated nodes themselves) convert to lit
 * `render()`.
 */
import { captureSerp, isSerpCurrent, serpRoot } from './mountRoot.ts';
import { html, nothing, render, type TemplateResult } from 'lit';
import { fetchTimeline, type TimelineResponse } from '../api/impact.ts';
import { assetForUrl, keyDomains } from '../derive/exec.ts';
import type { TermLocRow } from '../derive/search.ts';
import { AXES, CAT_LABEL, type SerpAxisInput } from '../derive/axes.ts';
import type { Page1Mode, SerpCapture, SerpElement } from '../types/contract.ts';
import { domainOf, favForDomain, faviconUrl, fixMojibake } from '../lib/format.ts';
import { chipT } from '../lib/templates.ts';
import { openDrawer } from '../ui/drawer.ts';
import { openResultPopover } from '../ui/popover.ts';
import { getSerpAxis, getSerpTerm, renderSearch, setSerpAxis, setSerpTerm, type SerpAxisKey } from './search.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

/* the full IMPACT capture for a term × location, if this run carries one */
function captureFor(state: AppPayloadState, term: string | null, location: string | null | undefined): SerpCapture | null {
  return (state.serps || []).find((s) =>
    fixMojibake(s.query) === term && (s.location || '') === (location || ''))
    || (state.serp && fixMojibake(state.serp.query) === term
        && (state.serp.location || '') === (location || '') ? state.serp : null);
}

/* one rendered card on the IMPACT canvas (a city, or a polled day) */
interface ImpCard {
  h1: string;
  h2: string;
  sub: string;
  term: string | null;
  els: SerpElement[] | null;
  composition: TermLocRow | null;
  /** W1-B finding 3: how this card's page-1 is grouped — 'reported' uses IMPACT's
   *  own page field; anything else falls back to base-10 (position-assumed). */
  page1Mode?: Page1Mode;
}

/** W1-B finding 3: is this organic element on page 1? When the capture is
 *  source-classified ('reported'), trust IMPACT's own page; otherwise fall back
 *  to the base-10 position-assumed rule (first 10 organic). */
function isOrganicPageOne(el: SerpElement, mode: Page1Mode | undefined, organicRank: number): boolean {
  if (mode === 'reported') return el.page === 1;
  return organicRank <= 10;
}

/* ── the unified IMPACT view: one card canvas, two compare modes (across
   cities / over time), three zoom viewports (regular / medium / small) ── */
interface TlState {
  mode: string;
  zoom: string;
  term: string | null;
  location: string | null;
  start: string | null;
  end: string | null;
  highlight: string;
  data: TimelineResponse | null;
  _rows?: TermLocRow[];
}
export const TL_STATE: TlState = { mode:'cities', zoom:'regular', term:null, location:null,
                 start:null, end:null, highlight:'', data:null };

export function wireImpControls(rows: TermLocRow[], state: AppPayloadState): void {
  const win: { start?: string; end?: string } = (state.impact && state.impact.window) || {};
  const locs = [...new Set(rows.filter((r) => r.term === getSerpTerm()).map((r) => r.location))];
  TL_STATE.term = getSerpTerm();
  if (!TL_STATE.location || !locs.includes(TL_STATE.location)) TL_STATE.location = locs[0] ?? null;
  const locSel = serpRoot().querySelector<HTMLElement>('#tlLoc') as HTMLSelectElement | null;
  if (locSel) render(html`${locs.map((l) => html`<option ?selected=${l===TL_STATE.location}>${l}</option>`)}`, locSel);
  const sEl = serpRoot().querySelector<HTMLElement>('#tlStart') as HTMLInputElement | null, eEl = serpRoot().querySelector<HTMLElement>('#tlEnd') as HTMLInputElement | null;
  if (sEl){ sEl.value = TL_STATE.start || win.start || ''; sEl.min = win.start || ''; sEl.max = win.end || ''; }
  if (eEl){ eEl.value = TL_STATE.end || win.end || ''; eEl.min = win.start || ''; eEl.max = win.end || ''; }
  (serpRoot().querySelector<HTMLElement>('#tlHighlight') as HTMLSelectElement).value = TL_STATE.highlight;
  syncTimeOnlyControls();

  serpRoot().querySelectorAll<HTMLButtonElement>('#modeSeg button').forEach(b => b.onclick = () => {
    TL_STATE.mode = b.dataset.m!;
    serpRoot().querySelectorAll('#modeSeg button').forEach(x => x.classList.toggle('on', x === b));
    syncTimeOnlyControls();
    refreshImpCanvas(rows, state);
  });
  serpRoot().querySelectorAll<HTMLButtonElement>('#zoomSeg button').forEach(b => b.onclick = () => {
    TL_STATE.zoom = b.dataset.z!;
    serpRoot().querySelectorAll('#zoomSeg button').forEach(x => x.classList.toggle('on', x === b));
    refreshImpCanvas(rows, state);
  });
  const axisSeg = serpRoot().querySelector<HTMLElement>('#axisSeg');
  if (axisSeg) axisSeg.querySelectorAll<HTMLButtonElement>('button').forEach(b => b.onclick = () => { setSerpAxis(b.dataset.a as SerpAxisKey); renderSearch(state, serpRoot() as HTMLElement); });
  const termSeg = serpRoot().querySelector<HTMLElement>('#termSeg');
  if (termSeg) termSeg.querySelectorAll<HTMLButtonElement>('button').forEach(b => b.onclick = () => { setSerpTerm(b.dataset.t!); TL_STATE.data = null; renderSearch(state, serpRoot() as HTMLElement); });
  if (locSel) locSel.onchange = () => { TL_STATE.location = locSel.value; loadTimeline(state); };
  if (sEl) sEl.onchange = () => { TL_STATE.start = sEl.value; loadTimeline(state); };
  if (eEl) eEl.onchange = () => { TL_STATE.end = eEl.value; loadTimeline(state); };
  (serpRoot().querySelector<HTMLElement>('#tlHighlight') as HTMLSelectElement).onchange = () => {
    TL_STATE.highlight = (serpRoot().querySelector<HTMLElement>('#tlHighlight') as HTMLSelectElement).value; refreshImpCanvas(rows, state);
  };
  const legend = serpRoot().querySelector<HTMLElement>('#axisLegend');
  if (legend) render(html`${AXES[getSerpAxis()].legend.map(([l,c]) => html`<span><i style="background:${c}"></i>${l}</span>`)}`, legend);
}
export function syncTimeOnlyControls(): void {
  serpRoot().querySelectorAll<HTMLElement>('.tl-time-only').forEach(x =>
    x.style.display = TL_STATE.mode === 'time' ? '' : 'none');
}
export function refreshImpCanvas(rows: TermLocRow[] | null | undefined, state: AppPayloadState): void {
  TL_STATE._rows = rows || TL_STATE._rows || [];
  if (TL_STATE.mode === 'cities'){
    serpRoot().querySelector<HTMLElement>('#tlStatus')!.textContent = '';
    renderImpCanvas(cityCards(TL_STATE._rows, state), state);
  } else if (TL_STATE.data && TL_STATE.data.term === getSerpTerm()
      && (TL_STATE.data.location||'') === (TL_STATE.location||'')){
    renderImpCanvas(timeCards(TL_STATE.data), state);
  } else loadTimeline(state);
}
/* across-cities cards: the latest capture per city from the run payload */
function cityCards(rows: TermLocRow[], state: AppPayloadState): ImpCard[] {
  const termRows = rows.filter((r) => r.term === getSerpTerm())
    .slice().sort((a,b) => ((b.owned ?? -1) - (a.owned ?? -1)) || ((a.negative ?? 0) - (b.negative ?? 0)));
  return termRows.map((r) => {
    const cap = captureFor(state, getSerpTerm(), r.location);
    const els = cap ? (cap.elements && cap.elements.length ? cap.elements
      : cap.results.map((x) => ({...x, organic:true, type:'standard'}))) : null;
    return { h1: r.location || '—',
      h2: cap ? `polled ${(cap.capturedAt||'').slice(0,10)}` : 'order not measured',
      sub: `${r.owned != null ? `${r.owned}/${r.slots} owned` : ''}${r.negative ? ` · ${r.negative} undesirable` : ''}`,
      term: getSerpTerm(), els, composition: cap ? null : r, page1Mode: cap?.page1Mode };
  });
}
function timeCards(d: TimelineResponse): ImpCard[] {
  return (d.polls || []).map((p) => {
    const els = (p.elements && p.elements.length) ? p.elements
      : p.results.map((x) => ({...x, organic:true, type:'standard'}));
    const mode = (p as { page1Mode?: Page1Mode }).page1Mode;
    let rank = 0;
    const p1 = els.filter((e) => e.organic !== false).filter((e) => isOrganicPageOne(e, mode, ++rank));
    const owned = p1.filter((x) => x.ownership === 'owned').length;
    const neg = p1.filter((x) => x.rating === 'negative').length;
    return { h1: new Date(p.capturedAt).toLocaleDateString('en-US',
        {weekday:'short', month:'short', day:'numeric', year:'numeric'}),
      h2: p.location || TL_STATE.location || '',
      sub: `${owned}/${p1.length} owned${neg ? ` · ${neg} undesirable` : ''}`,
      term: p.query || getSerpTerm(), els, composition: null, page1Mode: mode };
  });
}
export async function loadTimeline(state: AppPayloadState): Promise<void> {
  // FIX-audit F2: capture the render context (root + generation) at request
  // start and scope every DOM lookup to it; a slow IMPACT response for client A
  // must never paint into client B's now-current #tlGrid. Sub-lookups resolve
  // against the captured root, and the post-await commit is gated on
  // isSerpCurrent() — a superseded load discards its commit silently.
  const ctx = captureSerp();
  const st = ctx.root.querySelector<HTMLElement>('#tlStatus')!;
  st.textContent = 'loading from IMPACT…';
  // FIX-audit P0-1a: clear #tlGrid through the SINGLE owner (renderImpCanvas with
  // no cards) rather than a separate `render(html``, #tlGrid)` call site. Every
  // write to #tlGrid now flows through the one inner-wrapper rebuild, so a clear
  // here can never leave a stray lit part that a later renderImpCanvas appends to.
  renderImpCanvas([], state);
  try {
    const data = await fetchTimeline({ brand: (state.brand || '').split(' (')[0] ?? '', term: getSerpTerm() || '',
      location: TL_STATE.location || '', start: TL_STATE.start || '', end: TL_STATE.end || '', max_polls: '10' });
    if (!isSerpCurrent(ctx)) return; // superseded during the await — discard
    if (!data.available){ st.textContent = data.reason || 'IMPACT unavailable'; TL_STATE.data = null; return; }
    TL_STATE.data = data;
    st.textContent = '';
    renderImpCanvas(timeCards(data), state); // serpRoot() === ctx.root here (guarded)
  } catch(e){
    if (!isSerpCurrent(ctx)) return;
    st.textContent = 'service not reachable on :8000 — start it to browse the live history';
    TL_STATE.data = null;
  }
}
/* IMPACT's SERP-feature vocabulary (card/index.vue getElementType) */
export const IMP_FEATURE_LABEL: Record<string, string> = {
  standardkp:'Knowledge Panel', localkp:'Local KP', inlinekp:'Knowledge Panel',
  seeresultsabout:'See results about', newscontainer:'Top stories',
  videocontainer:'Videos', shortvideoscontainer:'Short videos',
  imagecontainer:'Images', twitterbox:'Posts on X', postsonxbox:'Posts on X',
  latestpostsbox:'Latest posts', questionanswerbox:'People also ask',
  faqbox:'FAQ', aioverview:'AI Overview', sitelinkbox:'Sitelinks',
  minisitelinksbox:'Sitelinks', profilepanel:'Profiles',
  perspectivesbox:'Perspectives', whatpeoplearesayingbox:'What people are saying',
  discussionsandforumsbox:'Discussions & forums', latestfrombox:'Latest from',
  featuredresultspanel:'Featured results', articlecontainer:'Articles',
  youtubecarousel:'YouTube carousel', booksbox:'Books', eventsbox:'Events',
  songsbox:'Songs', albumsbox:'Albums',
  thingstoknowbox:'Things to know', aboutcarousel:'About',
  locationcontainer:'Places', snippet:'Featured snippet',
};
/* markup color per IMPACT's _markup.scss: border = -dark, highlight fill = -bg */
function impMarkup(el: SerpAxisInput, state: AppPayloadState): [string | null | undefined, string | null] {
  if (getSerpAxis() === 'sentiment'){
    if (el.rating === 'positive') return ['var(--imp-pos)', 'var(--imp-pos-bg)'];
    if (el.rating === 'negative') return ['var(--imp-neg)', 'var(--imp-neg-bg)'];
    if (el.rating === 'neutral')  return ['var(--imp-soso)', 'var(--imp-soso-bg)'];
    return ['transparent', null];
  }
  if (getSerpAxis() === 'ownership'){
    if (el.ownership === 'owned') return ['var(--imp-owned)', 'var(--imp-owned-bg)'];
    if (el.ownership === 'resembler') return ['var(--imp-resembler)', 'var(--imp-resembler-bg)'];
    if (el.ownership === 'not_owned') return ['var(--imp-notowned)', 'var(--imp-notowned-bg)'];
    return ['transparent', null];
  }
  const [, color] = AXES.authority.of(el, assetForUrl(state, el.url));   // derived, not IMPACT markup
  return [color, null];
}

/* ONE renderer for both compare modes and all three zoom viewports.
   regular/medium = IMPACT's two-line result rows (medium drops favicons and
   shrinks per _sizes.scss); small = IMPACT's small-view favicon column, with
   the markup color as the row fill — the "city by city" overview. */
export function renderImpCanvas(cards: ImpCard[], state: AppPayloadState): void {
  const grid = serpRoot().querySelector<HTMLElement>('#tlGrid');
  const cap = serpRoot().querySelector<HTMLElement>('#tlCaption');
  if (!grid) return;
  grid.className = 'imp-canvas zoom-' + TL_STATE.zoom;
  const hl = TL_STATE.highlight;
  const small = TL_STATE.zoom === 'small';
  const hlMatch = (el: SerpElement) =>
    hl === 'owned' ? el.ownership === 'owned'
    : hl === 'negative' ? el.rating === 'negative'
    : hl === 'notowned' ? (el.organic !== false && el.ownership !== 'owned')
    : false;

  const rowFor = (el: SerpElement): TemplateResult => {
    const organic = el.organic !== false;
    if (!organic){
      const label = IMP_FEATURE_LABEL[el.type ?? ''] || el.type;
      if (small) return html`<div class="imp-result imp-feature sm" title="${label}"><div class="markup-container"><span class="ftype">◆</span></div></div>`;
      const items = el.items || [];
      // rich-capture features render typed blocks; features whose payload is
      // genuinely empty (e.g. locationcontainer) get a header-only bar so the
      // presence still shows without a stray floating pill. Legacy captures
      // (pre-feature payloads, e.g. El Al) keep the old one-line label row.
      const isRichCapture = items.length || el.kp || el.count !== undefined || (!el.title && !el.url);
      if (isRichCapture){
        const head = html`<div class="imp-fhead"><span class="ftype">${label}</span>${el.count ? html`<span class="fcount">${el.count}</span>` : nothing}</div>`;
        let body: TemplateResult | typeof nothing = nothing;
        if (el.kp && !items.length){
          body = html`<div class="imp-fitem qrow">${el.kp.img ? html`<img src="${el.kp.img}" style="width:22px;height:22px;border-radius:4px">` : nothing}<span>${fixMojibake((el.kp.bullets||[])[0] || 'Knowledge panel')}</span></div>`;
        } else if (el.type === 'sitelinkbox' || el.type === 'minisitelinksbox'){
          body = html`<div class="imp-sitelinks">${items.slice(0,4).map((i) => html`<span style="border-left-color:${impMarkup(i, state)[0]}" title="${fixMojibake(i.title||'')} — ${i.url||''}">${fixMojibake(i.title || domainOf(i.url) || '')}</span>`)}</div>`;
        } else if (items.some((i) => i.thumb)){
          body = html`<div class="imp-fthumbs">${items.filter((i) => i.thumb).slice(0,5).map((i) => html`<img src="${i.thumb}" alt="" title="${fixMojibake(i.title || i.desc || '')}">`)}</div>`;
        } else if (items.length){
          const isQ = /questionanswer|thingstoknow|faq/.test(el.type ?? '');
          body = html`${items.slice(0,4).map((i) => html`<div class="imp-fitem ${isQ ? 'qrow' : 'lrow'}" style="border-left-color:${impMarkup(i, state)[0]}" title="${fixMojibake(i.desc || i.title || '')}">${isQ ? html`<span class="q">?</span>` : (i.url ? html`<img src="${faviconUrl(i.url,32)}" alt="">` : nothing)}<span>${fixMojibake(i.title || i.desc || '')}</span></div>`)}`;
        }
        return html`<div class="imp-fblock${body === nothing ? ' empty' : ''}" title="${label}">${head}${body}</div>`;
      }
      return html`<div class="imp-result imp-feature" title="${label}${el.title ? ' — ' + fixMojibake(el.title) : ''}">
        <div class="markup-container"><span class="ftype">${label}</span>
        <span class="fdetail">${fixMojibake(el.title || '')}</span></div></div>`;
    }
    const [border, bg] = impMarkup(el, state);
    const nurl = (el.url || '').toLowerCase().replace(/^https?:\/\/(www\.)?/,'').replace(/\/$/,'');
    const tt = `${fixMojibake(el.title||'')} — ${el.url||''}`;
    if (small){
      const fillColor = border === 'transparent' ? 'var(--imp-soso-bg)' : border;
      const dim = hl && !hlMatch(el);
      return html`<div class="imp-result sm ${dim?'dim':''}" data-url="${el.url||''}" data-nurl="${nurl}" data-rating="${el.rating||''}" data-ownership="${el.ownership||''}" data-title="${fixMojibake(el.title||'')}" title="${tt}">
        <div class="markup-container" style="background:${fillColor}">
          ${el.url ? html`<img src="${faviconUrl(el.url,32)}" alt="">` : nothing}</div></div>`;
    }
    const fill = hl && hlMatch(el) && (bg || 'var(--imp-ui-header-bg)');
    return html`<div class="imp-result" data-url="${el.url||''}" data-nurl="${nurl}" data-rating="${el.rating||''}" data-ownership="${el.ownership||''}" data-title="${fixMojibake(el.title||'')}" title="${tt}">
      <div class="markup-container" style="border-left-color:${border}${fill ? `;background:${fill}` : ''}">
        <div class="rtitle">${el.url && TL_STATE.zoom === 'regular' ? html`<img src="${faviconUrl(el.url,32)}" alt="">` : nothing}${fixMojibake(el.title || domainOf(el.url) || '')}</div>
        <div class="rurl">${el.url || ''}</div>
      </div></div>`;
  };

  const cardTpls = cards.map((c) => {
    let body: TemplateResult | typeof nothing = nothing, axisHint = '';
    if (c.composition){
      // no per-result capture for this city — IMPACT measured only ownership +
      // negative COUNTS here. Paint only what the selected axis can honestly
      // claim from counts; never let the legend and canvas contradict (P0-2).
      const r = c.composition;
      const cell = (col: string) => html`<div class="imp-result sm"><div class="markup-container" style="background:${col}"></div></div>`;
      const owned = r.owned ?? 0, neg = r.negative ?? 0,
            rest = Math.max(0, (r.slots||10) - owned - neg);
      const seg: TemplateResult[] = [];
      if (getSerpAxis() === 'sentiment'){
        // the negative count IS a sentiment fact; owned/rest counts are not
        for (let i=0;i<neg;i++) seg.push(cell('var(--imp-neg)'));
        for (let i=0;i<owned+rest;i++) seg.push(cell('var(--imp-soso-bg)'));
        axisHint = 'negative count only — sentiment not captured for this city';
      } else if (getSerpAxis() === 'authority'){
        // counts carry no authority signal — render neutral, flag the gap
        for (let i=0;i<owned+neg+rest;i++) seg.push(cell('var(--imp-soso-bg)'));
        axisHint = 'ownership counts only — authority not captured for this city';
      } else {
        // ownership axis: the counts are exactly an ownership measurement
        for (let i=0;i<owned;i++) seg.push(cell('var(--imp-owned)'));
        for (let i=0;i<neg;i++) seg.push(cell('var(--imp-neg)'));
        for (let i=0;i<rest;i++) seg.push(cell('var(--imp-soso-bg)'));
      }
      body = html`${seg}`;
    }
    let hasKp = false;
    if (!c.composition){
      let organicN = 0;
      const p1Rows: TemplateResult[] = [], p2Rows: TemplateResult[] = [];
      // top knowledge panel (position 0) floats as the KP chip beside the
      // first rows — exactly how the real IMPACT card renders it — not as a row
      const topKp = small ? null : ((c.els || []).find((el) =>
        el.organic === false && !!el.kp && (el.position === 0 || el.position == null)) ?? null);
      hasKp = !!topKp;
      (c.els || []).forEach((el) => {
        if (el === topKp) return;
        const organic = el.organic !== false;
        if (organic) organicN++;
        // W1-B finding 3: page-1 membership from IMPACT's own page when the capture
        // is source-classified ('reported'), else the base-10 position-assumed rule.
        const onPageOne = !organic || isOrganicPageOne(el, c.page1Mode, organicN);
        if (small && organic && !onPageOne) return;   // small = page-1 column
        (organic && !onPageOne ? p2Rows : p1Rows).push(rowFor(el));
      });
      if (topKp && topKp.kp){
        // (`topKp && topKp.kp` ≡ the original `topKp` — the find() predicate
        // above only matches kp-bearing elements; restated so TS narrows.)
        const kp = topKp.kp;
        const bullets = kp.bullets || [];
        const kpTip = 'Knowledge Panel' + (bullets.length ? ' — ' + bullets.join(' · ') : '');
        p1Rows.unshift(html`<div class="imp-kpchip" style="${kp.img ? `background-image:url(${kp.img})` : ''}" title="${fixMojibake(kpTip)}">${kp.img ? '' : 'KP'}</div>`);
      }
      body = html`${p1Rows}${p2Rows.length ? html`
        <div class="imp-pagediv imp-p2toggle" title="show/hide page 2">page 2 · ${p2Rows.length} results <span class="p2arrow">▸</span></div>
        <div class="imp-p2wrap">${p2Rows}</div>` : nothing}`;
    }
    return html`<div class="imp-card${hasKp ? ' has-kp' : ''}" data-city="${TL_STATE.mode === 'cities' ? c.h1 : (c.h2 || '')}">
      <div class="imp-cardheader">
        <div class="ttl-date">${c.h1}</div>
        <div class="ttl-loc">${c.h2 || ''}${c.sub ? ` · ${c.sub}` : ''}${axisHint ? ` · ${axisHint}` : ''}</div>
        ${small ? nothing : html`<div class="term-field"><span class="glogo">G</span><span class="term-field-title">${fixMojibake(c.term)}</span></div>`}
      </div>
      <div class="imp-results">${body}</div>
    </div>`;
  });

  // FIX-audit P0-1a: paint the cards into a FRESH inner container this renderer
  // OWNS and rebuilds each call, rather than lit-rendering directly into the
  // shared fixed-id #tlGrid. #tlGrid is painted by several interleaving paths
  // (this sync cities re-render, loadTimeline's async clear/commit, and — after
  // a deep-page host re-render — a re-created grid); rendering lit straight into
  // it can orphan lit's ChildPart and make the next render APPEND a second card
  // tree (the ascensus Over-time→cities→zoom double: 627→1093). A throwaway
  // inner wrapper (display:contents, so cards still flex directly in #tlGrid) is
  // always a clean element with no prior lit part — a repaint can NEVER stack a
  // second tree. replaceChildren() first removes any content left by another
  // path so no orphaned markers survive.
  grid.replaceChildren();
  const inner = document.createElement('div');
  inner.className = 'imp-canvas-inner';
  grid.appendChild(inner);
  render(html`${cardTpls}`, inner);

  // wiring stays post-render/imperative — see the wave-C carve-out in this
  // file's doc comment (cross-card hover highlight is a whole-grid concern).
  grid.querySelectorAll<HTMLElement>('.imp-result[data-url]').forEach(elm => {
    elm.onclick = ev => openResultPopover(state, elm.dataset.url!, ev, {
      rating: elm.dataset.rating, ownership: elm.dataset.ownership,
      title: elm.dataset.title,
      city: elm.closest<HTMLElement>('.imp-card')?.dataset.city ?? null });
    // IMPACT's cross-card highlight: hovering a result lights the SAME url in
    // every card, so you track it across cities or across the window
    elm.onmouseenter = () => { const n = elm.dataset.nurl; if (!n) return;
      grid.querySelectorAll(`.imp-result[data-nurl="${CSS.escape(n)}"]`).forEach(x => x.classList.add('hl-cross')); };
    elm.onmouseleave = () => grid.querySelectorAll('.hl-cross').forEach(x => x.classList.remove('hl-cross'));
  });
  grid.querySelectorAll<HTMLElement>('.imp-p2toggle').forEach(t => t.onclick = () => {
    t.classList.toggle('open');
    const wrap = t.nextElementSibling; if (wrap) wrap.classList.toggle('open');
  });
  if (cap){
    // FIX-audit P0-1d: an EMPTY canvas (the loadTimeline loading/cleared state)
    // must not print a fabricated "0 of 0 polled days" caption — clear it. The
    // honest status ("no IMPACT history…") lives in #tlStatus, set by loadTimeline.
    if (!cards.length) {
      cap.textContent = '';
    } else {
      const modeTxt = TL_STATE.mode === 'cities'
        ? `one card per city, latest poll, best → worst ownership`
        : `${cards.length} of ${(TL_STATE.data && TL_STATE.data.totalPollDays) || cards.length} polled days (evenly sampled), oldest → newest · “${getSerpTerm()}” in ${TL_STATE.location || '—'}`;
      cap.textContent = `${modeTxt} · color = the axis selected above (IMPACT markup) · hover a result to track the same URL across every card · highlight ${small ? 'dims' : 'fills'} matching results · click a result for its asset record`;
    }
  }
}

export function renderKeyDomains(state: AppPayloadState): void {
  const tb = serpRoot().querySelector<HTMLElement>('#keyDomRows')!;
  const doms = keyDomains(state, 12);
  if (!doms.length){ render(html`<tr><td colspan="4" style="color:var(--muted)">No IMPACT polling history in this run.</td></tr>`, tb); return; }
  render(html`${doms.map((d) => {
    // majority category among all assets on this domain — one odd asset on a
    // shared domain (e.g. a defunct brand's Wikipedia page) must not flip the chip
    let matches = state.assets.filter((x) => x.url && domainOf(x.url) === d.domain);
    // a client SUBDOMAIN (app.elal.co.il) inherits its parent domain's asset —
    // the client's own domains must never read as "not in the asset map"
    if (!matches.length) matches = state.assets.filter((x) => x.url && d.domain.endsWith('.' + domainOf(x.url)));
    const counts: Record<string, number> = {}; matches.forEach((x) => counts[x.category] = (counts[x.category]||0)+1);
    const cat = Object.keys(counts).sort((a,b) => (counts[b]||0)-(counts[a]||0))[0] || null;
    const a = matches.find((x) => x.category === cat) || matches[0];
    return html`<tr class="${a?'click':''}" data-id="${a?a.id:nothing}" @click=${() => { if (a) openDrawer(state, a.id); }}>
      <td style="white-space:nowrap"><img class="favi" src="${favForDomain(d.domain)}" alt="">${d.domain}</td>
      <td>${a ? chipT(CAT_LABEL[a.category], a.category) : chipT('Not mapped','')}</td>
      <td class="num" title="${d.polls.toLocaleString()} tracked appearances across ${d.urls} URL${d.urls===1?'':'s'} of this domain">${d.share >= 9.5 ? Math.round(d.share) : d.share.toFixed(1)}%</td>
      <td class="num">${d.best < 1e9 ? '#'+Math.round(d.best) : '—'}</td>
    </tr>`;
  })}`, tb);
}
